import { fetchAuthSession } from 'aws-amplify/auth';
import type { AsnFailures, AsnFailuresQuery, AsnMetrics, AsnMetricsService } from '../types';
import { getAwsConfig } from '../../config/appConfig';
import { ensureAmplifyConfigured } from './amplifyConfig';

/** Resolve the API base + a bearer token, or throw with a clear message. */
async function resolveApi(): Promise<{ base: string; token: string }> {
    ensureAmplifyConfigured();

    const base = getAwsConfig().asnApiEndpoint;
    if (!base) {
        throw new Error('ASN API endpoint is not configured.');
    }

    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) {
        throw new Error('Not authenticated.');
    }
    return { base: base.replace(/\/$/, ''), token };
}

/**
 * ASN metrics from the service API (GET /v1/asn/metrics). Returns per-
 * integration counts (total / data errors / business errors) for the window,
 * authorized with the signed-in employee's Cognito token.
 */
export const awsAsnMetricsService: AsnMetricsService = {
    async getMetrics(from?: string, to?: string, identityId?: string): Promise<AsnMetrics> {
        const { base, token } = await resolveApi();

        const url = new URL(`${base}/v1/asn/metrics`);
        if (from) url.searchParams.set('from', from);
        if (to) url.searchParams.set('to', to);
        if (identityId) url.searchParams.set('identityId', identityId);

        const res = await fetch(url.toString(), {
            headers: { Authorization: token },
        });
        if (!res.ok) {
            throw new Error(`Failed to load ASN metrics: ${res.status} ${res.statusText}`);
        }
        return (await res.json()) as AsnMetrics;
    },

    async getFailures(query: AsnFailuresQuery): Promise<AsnFailures> {
        const { base, token } = await resolveApi();

        const url = new URL(`${base}/v1/asn/failures`);
        url.searchParams.set('source', query.source);
        url.searchParams.set('bucket', query.bucket);
        if (query.from) url.searchParams.set('from', query.from);
        if (query.to) url.searchParams.set('to', query.to);
        if (query.identityId) url.searchParams.set('identityId', query.identityId);
        if (query.limit) url.searchParams.set('limit', String(query.limit));
        if (query.cursor) url.searchParams.set('cursor', query.cursor);

        const res = await fetch(url.toString(), {
            headers: { Authorization: token },
        });
        if (!res.ok) {
            throw new Error(`Failed to load failures: ${res.status} ${res.statusText}`);
        }
        return (await res.json()) as AsnFailures;
    },
};

export default awsAsnMetricsService;
